import React, { FormEvent, useContext, useState, JSX } from 'react';
import { WorkflowContext } from "../../context/WorkflowContextProvider";
import Form from '@rjsf/mui';
import { IChangeEvent } from '@rjsf/core';
import {RegistryWidgetsType, RJSFSchema, UiSchema} from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { WorkflowNode, ToolOption, Workflow, Agent } from "../../../../types/workflow";
import { useReactFlow, applyNodeChanges, ReactFlowInstance, Edge, Node } from "@xyflow/react";
import { Tool } from "../../../../types/entities";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { processIfNodeDelete, processNodeFormSubmit, nodeDescription } from "./functions/workflow-if-node-configuration";
import { PromptTemplateFormWidget } from "./components/PromptTemplateFormWidget";
import { LLMModelWidget } from "./components/LLMModelWidget";
import { DefaultFormDialog  } from "./components/DefaultFormDialog";
import { NodeDataStoreSelectWidget } from "./components/NodeDataStoreSelectWidget";
import {
    Button
} from '@mui/material';

export const WorkflowIfNodeConfigure: () => JSX.Element = (): JSX.Element => {
    const context: any = useContext(WorkflowContext);
    const reactFlowInstance: ReactFlowInstance = useReactFlow();
    let edges: Edge[] = reactFlowInstance.getEdges();
    let ifSelectEdge: Edge = undefined;
    let ifNode: Node = undefined;
    for (const edge of edges) {
        if (context.editableIfNodeConfiguration !== undefined && edge.id.includes( '_[-if[in]->]_' + context.editableIfNodeConfiguration.id)) {
            ifSelectEdge = edge;
            break;
        }
    }

    if (ifSelectEdge !== undefined) {
        let nodes: Node[] = reactFlowInstance.getNodes();

        for (const node of nodes) {
            if (node.id === ifSelectEdge.source) {
                ifNode = node;
            }
        }
    }


    function handleCloseDialog(): void {
        context.hideShowIfModal(false, true);
    }

    function hasNodeName(): string {
        if (ifNode !== undefined && ifNode.data.label !== undefined && ifNode.data.label !== 'Untitled Node') {
            return 'Configure If Node '
        }
        return 'Edit If Node'
    }

    const handleFormSubmit: (data: IChangeEvent, event: FormEvent<any>) => void = (data: IChangeEvent, event: FormEvent<any>) : void => {
        console.log(data.formData);
        processNodeFormSubmit(context, data.formData, reactFlowInstance);
        handleCloseDialog();
    }

    function deleteNode(): void {
        processIfNodeDelete(context, reactFlowInstance);
        handleCloseDialog();
    }

    function prepareTools(): ToolOption[] {
        let returnTools: ToolOption[] = [];
        for (const tool of context.tools) {
            returnTools.push({
                title: tool.name,
                'const': tool.operatorToolId.toString()
            });
        }
        return returnTools;
    }

    const formSchema: RJSFSchema = {
        title: '',
        type: 'object',
        required: [],
        properties: {
            data_store: {
                'type': 'string',
                title: 'Data Input',
                'description': 'Data to be evaluated',
                default: 'default'
            },
            input_key: {
                'title': 'Data Value Key',
                type: 'string',
                description: 'Value for data model attributes, enter as value_one/value_two',
            },
            "if_type": {
                title: 'Action Type',
                enum: [
                    "Agent",
                    "Conditional"
                ],
                default: "Conditional",
            },

        },
        allOf: [
            {
                if: {
                    properties: {
                        "if_type": {
                            const: "Agent"
                        }
                    }
                },
                then: {
                    properties: {
                        system_prompt: {
                            title: 'Agent\'s - System Prompt',
                            type: 'string',
                            description: 'Describe what the agent does, give it some context. The agent will only return a true or false value',
                        },
                        action_prompt: {
                            title: 'Agent action prompt',
                            description: 'Let the Agent know which values you want to compare. The Agent will only return a true or false',
                            type: 'string',
                        },
                        tools: {
                            title: 'Tools',
                            type: 'array',
                            items: {
                                type: 'string',
                                anyOf: prepareTools()
                            },
                            uniqueItems: true,
                            description: 'Tools the Agent has access to',
                        },
                        llm_temperature: {
                            type: "number",
                            title: "LLM Temperature",
                            default: 0.7,
                            description: "The temperature of the LLM, higher values will result in more creative responses."
                        },
                        llm: {
                            title: 'LLM',
                            type: 'string',
                        },
                    }
                }
            },
            {
                if: {
                    properties: {
                        "if_type": {
                            const: "Conditional"
                        }
                    }
                },
                then: {
                    properties: {
                        if_condition: {
                            title: 'If Condition Type',
                            type: 'string',
                            enum: [
                                'AND',
                                'OR'
                            ],
                            default: 'AND'
                        },
                        evaluation_options: {
                            title: 'Evaluation Options',
                            type: 'array',
                            items: {
                                title: '',
                                type: 'object',
                                properties: {
                                    evaluation: {
                                        title: 'Conditional',
                                        type: 'string',
                                        enum: [
                                            'Equal To',
                                            'Not Equal To',
                                            'Greater Than',
                                            'Less Than',
                                            'Length Greater Than',
                                            'Length Less Than',
                                            'Length Equal To',
                                            'Is String',
                                            'Is Not String',
                                            'Is Array',
                                            'Is Not Array',
                                            'Is Data Model',
                                            'Is Not Data Model',
                                            'Is Number',
                                            'Is Not Number',
                                            'Is True',
                                            'Is False',
                                            'Parsable JSON'
                                        ],
                                        default: 'Equal To',
                                        description: 'The conditional type to evaluate the data input against. Length is for arrays or text'
                                    },
                                    evaluation_value: {
                                        title: 'Condition Value',
                                        type: 'string',
                                        description: 'The value to compare the data input against'
                                    }
                                }
                            }
                        },
                    }
                }
            },
        ]
    }

    const uiSchema: UiSchema = {
        'data_store': {
            'ui:widget': 'NodeDataManagementWidget',
            'ui:options': {
                'classNames': 'data-management',
                enumDisabled: [
                    'hq_params',
                    'data_to_loop',
                    'messages',
                ]
            }
        },
        'expected_value': {
            'ui:widget': 'textarea'
        },
        'system_prompt': {
            'ui:widget': 'PromptTemplateFormWidget'
        },
        'action_prompt': {
            'ui:widget': 'PromptTemplateFormWidget'
        },
        'llm': {
            'ui:widget': 'LLMModelWidget'
        },
        'data_management': {
            'ui:widget': 'NodeDataManagementWidget',
            'ui:options': {
                'classNames': 'data-management',
                enumDisabled: [
                    'hq_params',
                    'data_to_loop',
                    'messages',
                    'scraper_pending'
                ]
            }
        },

        'ui:order': [
            'data_store',
            'input_key',
            'if_type',
            'allOf',
            'system_prompt',
            'action_prompt',
            'tools',
            'data_management',
            'if_condition',
            'evaluation_options',
            'evaluation',
            'evaluation_value',
            'evaluation_key',
            'llm_temperature',
            'llm'
        ]
    }

    const widgets: RegistryWidgetsType = {
        PromptTemplateFormWidget: PromptTemplateFormWidget,
        LLMModelWidget: LLMModelWidget,
        NodeDataManagementWidget: NodeDataStoreSelectWidget
    }

    return (
        <DefaultFormDialog
            open={ context.showIfModal }
            title={ hasNodeName() }
            handleClose={ handleCloseDialog }
            description={ nodeDescription }
        >
            <span style={ { paddingLeft: '15px', fontWeight: 'bold' } }>{ "Node ID: " + context?.editableIfNodeConfiguration?.id }</span>
            <Form
                validator={validator}
                schema={ formSchema }
                uiSchema={ uiSchema }
                onSubmit={ handleFormSubmit }
                widgets={ widgets }
                formData={ (
                    context.editableIfNodeConfiguration !== undefined &&
                    context.editableIfNodeConfiguration.data !== undefined ?
                        context.editableIfNodeConfiguration.data : undefined
                ) }
            >
                <div className="dialog__actions form-group">
                    <Button type="submit"
                            variant="contained"
                            color="success"
                    >
                        <SaveIcon />
                        <span>{ "Save" }</span>
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={ deleteNode }
                        type="button">
                        <DeleteIcon />
                        <span>{ "Delete" }</span>
                    </Button>

                </div>
            </Form>
        </DefaultFormDialog>
    );

}